import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { notifyIntelliWriterAuthBridge } from "../utils/intelliWriterAuthBridge";

function getAccessTokenFromHash(hash: string): string | null {
  const normalizedHash = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(normalizedHash);
  return params.get("access_token");
}

const IntelliWriterAuthPage = () => {
  const [countdown, setCountdown] = useState(3);
  const [error, setError] = useState<string | null>(null);
  const didNotifyParent = useRef(false);

  useEffect(() => {
    const accessToken = getAccessTokenFromHash(window.location.hash);

    if (!accessToken) {
      setError("Authentication failed: no access token was returned.");
      return;
    }

    if (!window.opener) {
      setError("Authentication succeeded, but the IntelliWriter tab was not found.");
      return;
    }

    if (!didNotifyParent.current) {
      const didNotify = notifyIntelliWriterAuthBridge({
        type: "WA_AUTH_SUCCESS",
        token: accessToken,
      });

      if (!didNotify) {
        setError("Authentication succeeded, but IntelliWriter is not configured.");
        return;
      }

      didNotifyParent.current = true;
    }

    let remainingSeconds = 3;
    const intervalId = window.setInterval(() => {
      remainingSeconds -= 1;
      setCountdown(remainingSeconds);

      if (remainingSeconds <= 0) {
        window.clearInterval(intervalId);
        window.close();
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <Box className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
      <Paper elevation={0} className="w-full max-w-md p-8 text-center rounded-xl">
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box className="flex flex-col items-center gap-4">
            <CircularProgress size={32} />
            <Typography variant="h5">Authentication successful!</Typography>
            <Typography color="text.secondary">Closing in {countdown}...</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default IntelliWriterAuthPage;
